import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';

import { BetslipType } from '../../../core/betslip-type';
import { IComboBetState } from '../../combo-bet/state';
import { FreeBetToken, IRewardToken, RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { ISingleBetPickState } from '../../single-bet/state';
import { betslipTypeStateSelector } from '../../types/selectors';
import { IBetslipTypeState } from '../../types/state';
import { UnderMinimumStakePreCheckError } from '../../validation/errors/pre-check/under-minimum-stake-pre-check-error';
import { selectQuickBetContext, selectQuickBetRewardToken, selectQuickBetUnderMinStakeError } from '../quick-bet.selectors';
import { QuickBetStakeStatusService } from './quick-bet-stake-status.service';

export interface QuickBetPickDetails {
    rewardToken: IRewardToken | null;
    isEachWay: boolean;
    totalStake: number;
    tokenContext: RewardTokenContext;
    isReturnsVisible: boolean;
    underMinimumStakeError: UnderMinimumStakePreCheckError | null;
}

@Injectable()
export class QuickBetPickDetailsService {
    readonly model$: Observable<QuickBetPickDetails | null>;
    constructor(
        private store: Store,
        private quickBetStakeStatusService: QuickBetStakeStatusService,
    ) {
        const quickBetContext$ = this.store.select(selectQuickBetContext);
        const rewardToken$ = this.store.select(selectQuickBetRewardToken);
        const typeState$ = this.store.select(betslipTypeStateSelector);
        const underMinStake$ = this.store.select(selectQuickBetUnderMinStakeError);

        this.model$ = combineLatest([
            typeState$,
            quickBetContext$,
            rewardToken$,
            this.quickBetStakeStatusService.isQuickBetStakeActive$,
            underMinStake$,
        ]).pipe(
            map(([typeState, quickBetContext, rewardTokensState, isReturnsVisible, underMinimunStakeError]) =>
                this.getPickDetailsInternal(typeState, quickBetContext, rewardTokensState, isReturnsVisible, underMinimunStakeError),
            ),
        );
    }

    private getPickDetailsInternal(
        typeState: IBetslipTypeState,
        quickBetContext: ReturnType<typeof selectQuickBetContext>,
        selectedRewardToken: IRewardToken | null,
        isReturnsVisible: boolean,
        underMinimumStakeError: UnderMinimumStakePreCheckError | null,
    ): QuickBetPickDetails | null {
        if (quickBetContext.betslipType === BetslipType.Single) {
            const pick = typeState.singleBet.picks[quickBetContext.pickId.toString()];

            if (!pick) {
                return null;
            }

            const freeBet = isFreebetToken(selectedRewardToken) ? selectedRewardToken : null;
            const isEachWay = pick.isEachWay;
            const totalStake = this.getEachWayStake(pick, freeBet);

            return {
                rewardToken: selectedRewardToken,
                isEachWay,
                totalStake,
                tokenContext: quickBetContext,
                isReturnsVisible,
                underMinimumStakeError,
            };
        }
        if (quickBetContext.betslipType === BetslipType.BetBuilder) {
            const pick = typeState.betBuilder.picks[quickBetContext.pickId.toString()];

            if (!pick) {
                return null;
            }

            const freeBet = isFreebetToken(selectedRewardToken) ? selectedRewardToken : null;
            const isEachWay = false;
            const totalStake = freeBet?.amount ?? pick.stake;

            return {
                rewardToken: selectedRewardToken,
                isEachWay,
                totalStake: totalStake ?? 0,
                tokenContext: quickBetContext,
                isReturnsVisible,
                underMinimumStakeError,
            };
        }
        if (quickBetContext.betslipType === BetslipType.Combo) {
            const freeBet = isFreebetToken(selectedRewardToken) ? selectedRewardToken : null;

            return {
                rewardToken: selectedRewardToken,
                isEachWay: typeState.comboBet.isEachWay,
                totalStake: this.getEachWayStake(typeState.comboBet, freeBet),
                tokenContext: quickBetContext,
                isReturnsVisible,
                underMinimumStakeError,
            };
        }

        return null;
    }

    private getEachWayStake(pickState: ISingleBetPickState | IComboBetState, freebetToken: FreeBetToken | null): number {
        const isEachWay = pickState.isEachWay;

        const totalStake = freebetToken?.amount ?? (isEachWay ? (pickState?.stake ?? 0) * 2 : (pickState?.stake ?? 0));

        return totalStake;
    }
}
